import z from 'zod';
import {Logger} from '@practicegenius/domain/src/common/logger';
import {RedisClient} from '../server/redis';
import {generateRandomString} from '../server/tokens';

const CACHE_KEY_PREFIX = 'practice-user-login-migration_';
const TTL_SECONDS = 60 * 3; // 3 minutes

const practiceUserLoginMigrationDataSchema = z.object({
  /** Username credential provided during practice user login */
  username: z.string(),
  /** Password credential provided during practice user login */
  password: z.string(),
  /** Practice User Id corresponding to the above credentials. This is used as a sanity check and to influence the post-auth redirect */
  practiceUserId: z.number(),
});

export type PracticeUserLoginMigrationData = z.infer<
  typeof practiceUserLoginMigrationDataSchema
>;

type DataSetupConfig = {
  redisClient: RedisClient;
  logger: Logger;
};

export const practiceUserLoginMigrationTokenSchema = z
  .string()
  .brand<'PracticeUserLoginMigrationToken'>();
export type PracticeUserLoginMigrationToken = z.infer<
  typeof practiceUserLoginMigrationTokenSchema
>;

/** Create a one-time token that can be exchanged for the PracticeUserLoginMigrationData */
export const setupPracticeUserLoginMigrationData = async (
  config: DataSetupConfig,
  data: PracticeUserLoginMigrationData,
) => {
  const token = generateRandomString(16);

  await config.redisClient.set(
    `${CACHE_KEY_PREFIX}${token}`,
    JSON.stringify(data),
    {
      EX: TTL_SECONDS,
    },
  );

  return token as PracticeUserLoginMigrationToken;
};

/** Exchange the given token for the PracticeUserLoginMigrationData */
export const getPracticeUserLoginMigrationData = async (
  config: DataSetupConfig,
  token: PracticeUserLoginMigrationToken,
): Promise<PracticeUserLoginMigrationData | null> => {
  const data = await config.redisClient.getDel(`${CACHE_KEY_PREFIX}${token}`);

  if (!data) return null;
  const schemaParseResult = practiceUserLoginMigrationDataSchema.safeParse(
    JSON.parse(data),
  );
  if (!schemaParseResult.success) {
    config.logger.error(
      'Invalid practice user login migration data',
      schemaParseResult.error,
    );
    return null;
  }

  return schemaParseResult.data;
};
